import React, { Dispatch, useEffect, useState } from 'react';
import { checkIcon } from '@progress/kendo-svg-icons';
import { SvgIcon } from '@progress/kendo-react-common';
import { FileUpload } from '../FileUpload/FileUpload';
import { StipDocument } from '../../Models/StipDocument';
import { Stipulation } from '../../Models/Stipulation';
import styles from './_portal-stipulations.module.scss';
import { getStipulationCategoryForLanguage, getWordingForLanguage } from '../../Helpers/LanguageDictionary';
import { isNil, isNotNil } from 'ramda';
import { CustomerPortalUser } from '../../Models/CustomerPortalUser';
import { addStipDocumentsToDictionary, getStipulationCategories, getStipulationDocuments, getStipulations, updateStipDocument, uploadStipulationDocuments } from '../../Store/CustomerPortal/actions';
import { fetchConfig, httpPostAsync } from '../../Http/HttpClient';
import { useSelector } from 'react-redux';
import { AppState } from '../../Store/rootReducer';
import { StipulationCategory } from '../../Models/StipulationCategory';
import { Dialog } from '@progress/kendo-react-dialogs';
import { capitalizeFirstLetterOfEachWord } from '../../Helpers/Utilities';
import { CustomerRoles } from '../../Helpers/Constants';

export interface PortalStipulationsProps {
  disptach: Dispatch<any>;
  selectedLanguageId: string;
  clientId: string | null;
  canAddDocs: boolean; // whether the user can add documents
  canEditDocs: boolean; // whether the user can edit documents
  user: CustomerPortalUser;
  selectedApplicationCustomerId: string;
  width: number; // widtho the user's screen
}

interface ImageViewerDialogData {
  documentName?: string; // name of the document
  objectUrl?: string; // URL to the image to display
  open: boolean; // whether the dialog is open
}

export const PortalStipulations = (props: PortalStipulationsProps) => {
  // stipulation categories
  const stipulationCategories: StipulationCategory[] = useSelector((appState: AppState) => appState.customerPortal.stipulationCategories);
  // stipulations from redux store
  const stipulations: Stipulation[] = useSelector((appState: AppState) => appState.customerPortal.stipulations);
  // stipDocuments in redux store
  const stipulationDocuments: StipDocument[] = useSelector((appState: AppState) => appState.customerPortal.stipulationDocuments);
  const stipDocumentUrlDictionary: { [key: string]: string } = useSelector((appState: AppState) => appState.customerPortal.stipDocumentUrlDictionary);
  // config api endpoint
  const [configApiEndpoint, setConfigApiEndpoint] = useState<string>('');
  // whether or not to show the image viewer dialog
  const [showImageViewerDialog, setShowImageViewerDialog] = useState<ImageViewerDialogData>({ open: false });
  // whether or not to show the pdf viewer dialog
  const [showPdfViewerDialog, setShowPdfViewerDialog] = useState<ImageViewerDialogData>({ open: false });
  // Customer portal user
  const user: CustomerPortalUser = useSelector((appState: AppState) => appState.customerPortal.user);

  // useEffect to fetch config api endpoint on component mount
  useEffect(() => {
    props.disptach(getStipulationDocuments(props.user.applicationId, props.user.customerPortalLinkId, props.user.applicationCustomers.find(c => c.id === props.selectedApplicationCustomerId)!.customerId));
    props.disptach(getStipulations(props.user.customerPortalLinkId));
    props.disptach(getStipulationCategories(props.user.customerPortalLinkId));
    getConfig().then((apiEndpoint) => {
      setConfigApiEndpoint(apiEndpoint);
    });
  }, [props]);

  // returns true if this user is the primary applicant, false otherwise.
  const isPrimaryApplicant = () => {
    return props.user.applicationCustomers.find(c => c.id === props.selectedApplicationCustomerId) ? props.user.applicationCustomers.find(c => c.id === props.selectedApplicationCustomerId)!.roleId.toLowerCase() === CustomerRoles.applicant.toLowerCase() : false;
  }


  const downloadFile = async (appStipDocumentId: string) => {
    const response: Response | undefined = await httpPostAsync(`StipulationDocument/download?customerPortalLinkId=${props.user.customerPortalLinkId}`, { appCustomerStipDocumentId: appStipDocumentId });
    if (!response) {
      return;
    }
    const blob = await response.blob();
    return window.URL.createObjectURL(blob);
  }

  // async function to download the file from the URL. This is used to display the file image preview via a preview URL
  const downloadFiles = async () => {
    const items: { stipDocumentId: string, objectUrl: string }[] = [];
    // iterate through each stipulation document and download its file
    for (let stipDocument of stipulationDocuments?.filter(sd => isNotNil(sd.id))) {
      // ensure the file has not already been downloaded
      if (isNil(stipDocumentUrlDictionary[stipDocument.id!])) {
        const objectUrl = await downloadFile(stipDocument.id!);
        // if a file was downloaded, add it to the objectUrls dictionary
        if (objectUrl) {
          items.push({ stipDocumentId: stipDocument.id!, objectUrl });
        }
      }
    }
    props.disptach(addStipDocumentsToDictionary(items));
  };

  // when list of stipulation documents changes, download the files
  useEffect(() => {
    downloadFiles();
  }, [stipulationDocuments]);

  // returns the api endpoint from the config.json file
  const getConfig = async () => {
    const config: { api_endpoint: string } = await fetchConfig();
    return config.api_endpoint;
  }

  // rename stip document event handler
  const onEditStipDoc = (stipDocumentId: string, documentName: string, stipulationId: number) => {
    props.disptach(updateStipDocument(stipDocumentId, props.selectedApplicationCustomerId, props.user.customerPortalLinkId, documentName, stipulationId));
  }

  // Message notifying user that the stip category has been satisfied. This means that the user has uploaded at least the min required number of documents for the stip category.
  const showDocumentSatisfied = (stip: StipulationCategory): boolean => {
    if (isPrimaryApplicant() === true) {
      return (isNotNil(stip.numberRequiredForSubmission) && stip.numberRequiredForSubmission > 0
        && stipulationDocuments.filter((sd) => stipulations.filter((s) => s.stipulationCategoryId === stip.id).map((s) => s.id).includes(sd.stipulationId))?.length >= stip.numberRequiredForSubmission)
        || (isNil(stip.numberRequiredForSubmission) && stipulationDocuments.filter((sd) => stipulations.filter((s) => s.stipulationCategoryId === stip.id).map((s) => s.id).includes(sd.stipulationId))?.length > 0);
    } else {
      return (isNotNil(stip.numberRequiredForCoApplicantSubmission) && stip.numberRequiredForCoApplicantSubmission > 0
        && stipulationDocuments.filter((sd) => stipulations.filter((s) => s.stipulationCategoryId === stip.id).map((s) => s.id).includes(sd.stipulationId))?.length >= stip.numberRequiredForCoApplicantSubmission)
        || (isNil(stip.numberRequiredForCoApplicantSubmission) && stipulationDocuments.filter((sd) => stipulations.filter((s) => s.stipulationCategoryId === stip.id).map((s) => s.id).includes(sd.stipulationId))?.length > 0);
    }
  };

  // Message showing the docs are optional. This means that the stip category has no min required number of documents
  const showDocumentOptional = (stip: StipulationCategory): boolean => {
    return isPrimaryApplicant() === true ? (showDocumentSatisfied(stip) !== true && (isNil(stip.numberRequiredForSubmission) || stip.numberRequiredForSubmission === 0))
      : (showDocumentSatisfied(stip) !== true && (isNil(stip.numberRequiredForCoApplicantSubmission) || stip.numberRequiredForCoApplicantSubmission === 0));
  }

  // Message notifying user that more docs are required for the stip category
  const showUploadMoreDocsWarning = (stip: StipulationCategory): boolean => {
    if (isPrimaryApplicant() === true) {
      return isNotNil(stip.numberRequiredForSubmission) && ((
        stipulationDocuments.filter((sd) => stipulations.filter((s) => s.stipulationCategoryId === stip.id).map((s) => s.id).includes(sd.stipulationId))?.length < stip.numberRequiredForSubmission));
    } else {
      return isNotNil(stip.numberRequiredForCoApplicantSubmission) && ((
        stipulationDocuments.filter((sd) => stipulations.filter((s) => s.stipulationCategoryId === stip.id).map((s) => s.id).includes(sd.stipulationId))?.length < stip.numberRequiredForCoApplicantSubmission));
    }
  };

  // returns the dialog title, which is the document name. truncates the document name if it is too long (20 characters)
  const getDialogTitle = (documentName: string | undefined): string => {
    return documentName ? documentName.length > 20 ? documentName.substring(0, 20) + '...' : documentName : '';
  }
  const customerId = props.user.applicationCustomers.find(c => c.id === props.selectedApplicationCustomerId)!.customerId;
  return (
    <div className={styles.uploadStipsContainer}>
      <h3 style={{ marginBottom: 0, marginTop: 0 }}>
        {getWordingForLanguage('uploadDocsTitle', props.selectedLanguageId)}
      </h3>
      <p>
        {getWordingForLanguage('uploadDocsDescription', props.selectedLanguageId)}{user.assignedEmployeeStoreName}{'.'}
      </p>
      {stipulationCategories.sort((a: StipulationCategory, b: StipulationCategory) => (a.sortOrder || 0) - (b.sortOrder || 0)).map(
        (stipCategory: StipulationCategory, index) => {
          return (
            <div key={index} className={styles.stipItemContainer}>
              <div className={styles.stipTitle}>
                <b style={{ fontSize: '1.1em', margin: ' 0 1em .5em 0' }}>{capitalizeFirstLetterOfEachWord(getStipulationCategoryForLanguage(stipCategory.name.toLowerCase(), props.selectedLanguageId))}</b>
                {showDocumentSatisfied(stipCategory) && (
                  <div className={styles.stipComplete}>
                    <SvgIcon
                      icon={checkIcon}
                      size="medium"
                      style={{ color: 'darkgreen' }}
                    />
                  </div>
                )}
                {showDocumentOptional(stipCategory) && (
                  <div className={styles.stipOptional}>
                    {getWordingForLanguage('optional', props.selectedLanguageId)}
                  </div>
                )}
                {showUploadMoreDocsWarning(stipCategory) && (
                  <div className={styles.stipIncomplete}>
                    {(isPrimaryApplicant() ? stipCategory.numberRequiredForSubmission! : stipCategory.numberRequiredForCoApplicantSubmission!) -
                      stipulationDocuments.filter(
                        (sd) => sd.stipulationId === stipCategory.id
                      ).length}{' '}
                    {getWordingForLanguage('document', props.selectedLanguageId)}
                    {(isPrimaryApplicant() ? stipCategory.numberRequiredForSubmission! : stipCategory.numberRequiredForCoApplicantSubmission!) -
                      stipulationDocuments.filter(
                        (sd) => sd.stipulationId === stipCategory.id
                      ).length >
                      1 && 's'}{' '}
                    {getWordingForLanguage('needed', props.selectedLanguageId)}
                  </div>
                )}
              </div>
              {stipCategory.description && (
                <span className={styles.stipDescription}>{stipCategory.description}</span>
              )}
              <FileUpload
                key={index}
                stiplations={stipulations.filter((s) => s.stipulationCategoryId === stipCategory.id)}
                files={stipulationDocuments.filter((sd) => stipulations.filter((s) => s.stipulationCategoryId === stipCategory.id).map((s) => s.id).includes(sd.stipulationId)).map((sd) => {
                  return {
                    ...sd,
                    objectUrl: stipDocumentUrlDictionary[sd.id!]
                  }
                })}
                canAddDocs={props.canAddDocs}
                canEditDocs={props.canEditDocs}
                allowedExtensions={['.jpg', '.jpeg', '.pdf', '.png']}
                selectedLanguageId={props.selectedLanguageId}
                uploadUrl={`${configApiEndpoint}/StipulationDocument/uploadStipulationDocuments/${customerId}?clientId=${props.clientId}&customerPortalLinkId=${props.user.customerPortalLinkId}`}
                applicationCustomerId={props.selectedApplicationCustomerId}
                onEditStipDocument={onEditStipDoc}
                onViewImage={(objectUrl: string, documentName: string) => setShowImageViewerDialog({ documentName, objectUrl, open: true })}
                onViewPdf={(url: string, documentName: string) => setShowPdfViewerDialog({ documentName, objectUrl: url, open: true })}
                onUploadStipulationDocuments={(stipulationDocuments: StipDocument[]) => {
                  // dispatch action to update the redux store
                  props.disptach(uploadStipulationDocuments(stipulationDocuments));
                }}
              />
            </div>
          );
        }
      )}
      {showImageViewerDialog.open && (
        <Dialog title={getDialogTitle(showImageViewerDialog.documentName)} onClose={() => setShowImageViewerDialog({ documentName: undefined, objectUrl: undefined, open: false })}>
          <img src={showImageViewerDialog.objectUrl} alt={showImageViewerDialog.documentName} style={{ width: '100%', maxHeight: 'calc(100vh - 100px)' }} />
        </Dialog>
      )}
      {showPdfViewerDialog.open && (
        <Dialog contentStyle={props.width < 600 ? { height: 'calc(100vh - 160px)' } : { height: 'calc(100vh - 100px)' }} title={getDialogTitle(showPdfViewerDialog.documentName)} onClose={() => setShowPdfViewerDialog({ documentName: undefined, objectUrl: undefined, open: false })}>
          <iframe src={showPdfViewerDialog.objectUrl} style={props.width < 600 ? { width: '100%', height: 'calc(100vh - 160px)' } : { width: '100%', height: 'calc(100vh - 100px)' }} title={showPdfViewerDialog.documentName} />
        </Dialog>
      )}
    </div>
  );
};
